import type { ApplicationResourceListResponseV2Api } from '@ingenico/api-sdk-device-management-private-v2';
import type { ApplicationResourceApi } from '@ingenico/api-sdk-device-management-public';
import { enumVersionPublicationStatus } from '@ingenico/api-sdk-device-management-public';
import type { SearchFieldDetailApi } from '@ingenico/api-sdk-transaction-public';
import type { FieldConfiguration } from '@ingenico/react-ui/advanced-search';
import { isEnumValue } from '@ingenico/util-common';
import { toArray, toString } from 'lodash-es';
import type { ApplicationResourceProvider, PaginatedApplicationList } from '../../models/applicationList.model';
import { ApplicationResourceStatus } from '../../models/applicationList.model';
import type { SelectItems } from '../../models/selectItems.model';

export function mapApplicationListResponseApiToPaginatedApplicationList({
  data,
  ...rest
}: ApplicationResourceListResponseV2Api): PaginatedApplicationList {
  return {
    ...rest,
    data: data.map((item) => ({
      id: toString(item.id),
      name: toString(item.name),
      shortDescription: item.shortDescription,
      longDescription: item.longDescription,
      status: isEnumValue(item.status, enumVersionPublicationStatus)
        ? ApplicationResourceStatus[item.status]
        : ApplicationResourceStatus.DEPRECATED,
      creationDate: item.creationDate,
      provider: mapApplicationResourceApiProviderToApplicationResourceProvider(item.provider),
      compatibleDeviceFamilyNames: toArray(item.compatibleDeviceFamilyNames),
      applicationVersionIds: item.applicationVersions.reduce(
        (acc, appVersion) => [...acc, ...(appVersion.applicationVersionId ? [appVersion.applicationVersionId] : [])],
        [] as string[],
      ),
      assets: item.assets,
    })),
  };
}

export function mapApplicationsToSelectItems({ data }: ApplicationResourceListResponseV2Api): SelectItems[] {
  return data.map(({ id, name }) => ({ id: toString(id), label: toString(name) }));
}

function mapApplicationResourceApiProviderToApplicationResourceProvider(
  provider: ApplicationResourceApi['provider'],
): ApplicationResourceProvider | undefined {
  if (provider?.name && provider?.location) {
    return {
      name: provider.name,
      location: {
        city: toString(provider.location.city),
        address: toString(provider.location.address),
        country: toString(provider.location.country),
        state: toString(provider.location.state),
        zipCode: toString(provider.location.zipCode),
      },
    };
  }
  return undefined;
}

export function mapApplicationListToFieldConfiguration(response: SearchFieldDetailApi[]): FieldConfiguration[] {
  return response.map(({ key, type }) => ({
    key,
    type,
    visible: true,
    searchable: !key.includes('parent'),
  })) as FieldConfiguration[];
}
